import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../../auth";
import { BUILDER_BASE } from "./formBuilder/constants";
import Card from "./formBuilder/Card";
import FieldHint from "./formBuilder/FieldHint";
import { hintsPt } from "./formBuilder/hintsPt";

export default function FormBuilderListPage() {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [builderToast, setBuilderToast] = useState(null);
  const [newForm, setNewForm] = useState({
    title: "",
    description: "",
    code: "",
    target_group: "graduate",
    active: true,
  });

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/");
  };

  async function loadForms() {
    setLoading(true);
    setError("");
    try {
      const resp = await authFetch(`${BUILDER_BASE}/forms/`);
      if (!resp.ok) throw new Error(`Erro ${resp.status}`);
      const data = await resp.json();
      setForms(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || "Erro ao carregar formulários");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadForms();
  }, []);

  useEffect(() => {
    if (!builderToast) return;
    const id = setTimeout(() => setBuilderToast(null), 4200);
    return () => clearTimeout(id);
  }, [builderToast]);

  function showBuilderToast(type, message) {
    setBuilderToast({ type, message });
  }

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    setError("");
    try {
      const resp = await authFetch(`${BUILDER_BASE}/forms/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newForm),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `Erro ${resp.status}`);
      }
      setNewForm({
        title: "",
        description: "",
        code: "",
        target_group: "graduate",
        active: true,
      });
      await loadForms();
      showBuilderToast("success", "Formulário criado.");
    } catch (e) {
      const msg = e?.message || "Erro ao criar formulário";
      setError(msg);
      showBuilderToast("error", msg);
    } finally {
      setCreating(false);
    }
  }

  async function handleDeleteForm(e, form) {
    e.stopPropagation();
    if (
      !window.confirm(
        `Remover o formulário "${form.title}" (${form.code})? Perguntas, respostas e dados associados no servidor podem ser afectados ou impedir esta acção. Esta operação não pode ser desfeita.`
      )
    ) {
      return;
    }
    setDeletingId(form.id);
    try {
      const resp = await authFetch(`${BUILDER_BASE}/forms/${form.id}/`, {
        method: "DELETE",
      });
      if (!resp.ok && resp.status !== 204) {
        const txt = await resp.text();
        throw new Error(txt || `Erro ${resp.status} ao apagar formulário`);
      }
      await loadForms();
      showBuilderToast("success", "Formulário removido.");
    } catch (err) {
      const msg = err?.message || "Erro ao apagar formulário";
      showBuilderToast("error", msg);
    } finally {
      setDeletingId(null);
    }
  }

  function openEditor(form) {
    navigate(`/formularios/${form.id}/edit`);
  }

  return (
    <div className="min-h-screen max-w-7xl mx-auto pt-3 md:pt-4 pb-28 md:pb-24 px-4 sm:px-5 md:px-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-xs sm:text-sm px-3 py-2 rounded-lg border border-gray-300 text-gray-700 bg-white hover:border-emerald-400 hover:text-emerald-700 transition-colors"
          >
            ← Voltar
          </button>
          <h1 className="mt-3 text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Formulários</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">{hintsPt.listPageSubtitle}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <div className="md:col-span-1">
          <Card title="Novo formulário">
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Título</label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={newForm.title}
                  onChange={(e) => setNewForm((f) => ({ ...f, title: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  rows={3}
                  value={newForm.description}
                  onChange={(e) => setNewForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Código</label>
                  <FieldHint>{hintsPt.formCode}</FieldHint>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    value={newForm.code}
                    onChange={(e) => setNewForm((f) => ({ ...f, code: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Grupo-alvo</label>
                  <FieldHint>{hintsPt.formTargetGroup}</FieldHint>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    value={newForm.target_group}
                    onChange={(e) => setNewForm((f) => ({ ...f, target_group: e.target.value }))}
                  >
                    <option value="graduate">Graduados</option>
                    <option value="employer">Empregadores</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="form-active"
                  type="checkbox"
                  className="h-4 w-4 text-emerald-600 border-gray-300 rounded"
                  checked={newForm.active}
                  onChange={(e) => setNewForm((f) => ({ ...f, active: e.target.checked }))}
                />
                <label htmlFor="form-active" className="text-xs text-gray-700 select-none">
                  Activo
                </label>
              </div>
              <FieldHint>{hintsPt.formActive}</FieldHint>
              <button
                type="submit"
                disabled={creating}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed w-full"
              >
                {creating ? "A criar…" : "Criar formulário"}
              </button>
            </form>
          </Card>
        </div>

        <div className="md:col-span-2">
          <Card
            title="Formulários existentes"
            right={
              <button type="button" onClick={loadForms} className="text-xs text-emerald-700 hover:underline">
                Actualizar
              </button>
            }
          >
            {loading && <div className="text-sm text-gray-500">A carregar…</div>}
            {error && <div className="text-sm text-red-600 mb-2 break-words">{error}</div>}
            {!loading && forms.length === 0 && !error && (
              <div className="text-sm text-gray-500">{hintsPt.listEmptyForms}</div>
            )}
            {!loading && forms.length > 0 && (
              <div className="overflow-x-auto -mx-2">
                <table className="min-w-full text-sm text-left mx-2">
                  <thead>
                    <tr className="border-b border-gray-200 text-xs text-gray-500 uppercase">
                      <th className="py-2 pr-4">Título</th>
                      <th className="py-2 pr-4">Código</th>
                      <th className="py-2 pr-4">Grupo</th>
                      <th className="py-2 pr-4 text-center">Activo</th>
                      <th className="py-2 pr-2 text-right w-24">Acções</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forms.map((f) => (
                      <tr
                        key={f.id}
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                        onClick={() => openEditor(f)}
                      >
                        <td className="py-2 pr-4">
                          <div className="font-medium text-gray-900">{f.title}</div>
                          {f.description && (
                            <div className="text-xs text-gray-500 line-clamp-2">{f.description}</div>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-xs text-gray-700">{f.code}</td>
                        <td className="py-2 pr-4 text-xs text-gray-700">
                          {f.target_group === "employer" ? "Empregadores" : "Graduados"}
                        </td>
                        <td className="py-2 pr-4 text-center">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                              f.active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {f.active ? "Activo" : "Inactivo"}
                          </span>
                        </td>
                        <td className="py-2 pr-2 text-right">
                          <button
                            type="button"
                            onClick={(e) => handleDeleteForm(e, f)}
                            disabled={deletingId === f.id}
                            className="text-xs text-red-600 hover:underline disabled:opacity-50"
                          >
                            {deletingId === f.id ? "…" : "Apagar"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>

      {builderToast && (
        <div
          role="status"
          aria-live="polite"
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[1300] max-w-md w-[calc(100%-2rem)] px-4 py-3 rounded-xl shadow-lg border text-sm font-medium ${
            builderToast.type === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-red-50 border-red-200 text-red-900"
          }`}
        >
          {builderToast.message}
        </div>
      )}
    </div>
  );
}
